import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useParams, useNavigate } from "react-router-dom";
import {
  LogOut, ChevronDown, Bell, Search, ShieldCheck, Sparkles,
  ChevronRight, Menu, X, GraduationCap,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { NAV_BY_ROLE, ROLE_LABEL } from "../lib/nav";
import { toast } from "sonner";
import TenantCrest from "../components/TenantCrest";

function Crest({ tenant }) {
  return <TenantCrest tenant={tenant} size="md" testId="portal-crest" />;
}

const ROLE_TAGLINE = {
  executive: "Executive Intelligence Centre",
  principal: "Principal's Command Deck",
  deputy_head: "Deputy Head's Workspace",
  board: "Board Intelligence",
  administrator: "School Administration",
  teacher: "Teacher Workspace",
  parent: "Family Portal",
  student: "Learner Portal",
  finance: "Finance Command",
  admissions: "Admissions Command",
  boarding: "Boarding & Pastoral",
  health: "Clinic & Wellbeing",
  transport: "Transport Operations",
  librarian: "Library Services",
  hr: "People & Culture",
};

export default function PortalLayout() {
  const { user, tenant, logout } = useAuth();
  const { school } = useParams();
  const navigate = useNavigate();
  const items = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.executive;
  const [mobileOpen, setMobileOpen] = useState(false);
  const base = `/education360/${school}`;

  useEffect(() => { setMobileOpen(false); }, [items?.length]);

  const primary = tenant?.branding?.primary_color || "#002147";
  const accent = tenant?.branding?.accent_color || "#D9531E";
  const tagline = ROLE_TAGLINE[user?.role] || "Education360™";

  // Update tab title with the tenant name for a fully-branded browser session.
  useEffect(() => {
    if (tenant?.name) {
      document.title = `${tenant.name} · Education360`;
    }
  }, [tenant?.name]);

  // Tenant brand tokens are injected as CSS variables scoped to the portal
  // root so every scoped .edu360-root style automatically follows the
  // tenant's palette (navy + gold for Highlands) — no global overrides.
  const brandVars = {
    "--hl-navy": primary,
    "--hl-orange": accent,
    "--hl-orange-soft": tenant?.branding?.accent_color_soft || "rgba(201,162,39,0.10)",
  };

  const handleLogout = () => {
    logout();
    toast.success("Signed out");
    navigate(`/education360/${school}`);
  };

  return (
    <div className="edu360-portal min-h-screen flex bg-[color:var(--hl-page)] text-slate-800" style={brandVars}>
      {/* ============ Sidebar ============ */}
      <aside
        className={`edu360-sidebar ${mobileOpen ? "open" : ""} flex flex-col fixed md:sticky top-0 left-0 z-40 w-72 h-screen`}
        data-testid="edu360-sidebar"
        style={{ background: `linear-gradient(180deg, ${primary} 0%, #001432 60%, #000a1c 100%)` }}
      >
        {/* Ambient particles */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="absolute rounded-full bg-white/10"
              style={{ width: `${3 + i}px`, height: `${3 + i}px`,
                left: `${(i * 47) % 100}%`, top: `${(i * 31) % 100}%`,
                animation: `edu-float ${22 + i * 2}s ease-in-out ${i * 1.3}s infinite` }} />
          ))}
        </div>

        {/* Brand */}
        <div className="relative px-5 py-6 flex items-center gap-3 border-b border-white/10">
          <Crest tenant={tenant} />
          <div className="min-w-0">
            <div className="text-white font-display font-bold leading-tight text-[15px] truncate" data-testid="sidebar-school-name">
              {tenant?.short_name || tenant?.name || "School"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mt-0.5">Education360™</div>
          </div>
          <button className="md:hidden absolute right-3 top-6 text-white/60 hover:text-white" onClick={() => setMobileOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Role banner */}
        <div className="relative px-5 py-3 border-b border-white/5">
          <div className="text-[9px] uppercase tracking-[0.28em] font-bold" style={{ color: accent }}>
            {ROLE_LABEL[user?.role] || user?.role}
          </div>
          <div className="text-white/85 text-[12px] font-semibold mt-0.5">{tagline}</div>
        </div>

        {/* Nav */}
        <nav className="relative flex-1 overflow-y-auto py-3 px-2">
          {items.map((m) => (
            <NavLink
              key={m.id}
              to={`${base}/${m.path}`}
              end={m.path === ""}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `edu360-nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${m.id}`}
              style={({ isActive }) => isActive ? { background: `linear-gradient(90deg, ${accent}25 0%, transparent 100%)`, borderLeftColor: accent } : {}}
            >
              <m.icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{m.label}</span>
              <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-70 nav-chev" />
            </NavLink>
          ))}
        </nav>

        {/* Trust footer */}
        <div className="relative px-5 py-4 border-t border-white/10 text-[10px] text-white/40 uppercase tracking-[0.24em] space-y-1">
          <div className="flex items-center gap-1.5 text-white/60"><ShieldCheck className="w-3 h-3" style={{ color: accent }} />Powered by Education360™</div>
          <div className="text-white/30 normal-case tracking-normal text-[10px]">A THOM Digital Infrastructure platform</div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/50 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* ============ Main column ============ */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="edu360-topbar sticky top-0 z-20 backdrop-blur-xl bg-white/85 border-b border-slate-200" data-testid="edu360-header">
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button className="md:hidden p-1.5 text-slate-500 hover:text-slate-800" onClick={() => setMobileOpen(true)} data-testid="mobile-menu">
                <Menu className="w-5 h-5" />
              </button>
              <div className="hidden sm:block truncate">
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-semibold">{tenant?.name}</div>
                <div className="text-xs text-slate-600 flex items-center gap-2 mt-0.5">
                  <span>AY <span className="mono font-semibold text-slate-800">{tenant?.academic_year}</span></span>
                  <span className="text-slate-300">·</span>
                  <span>{tenant?.current_term}</span>
                  {tenant?.city && (<><span className="text-slate-300">·</span><span>{tenant.city}, {tenant.country}</span></>)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative hidden lg:block">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="header-search"
                  placeholder="Search students, staff, notices…"
                  className="pl-9 pr-4 py-2 text-sm bg-slate-50/80 border border-slate-200 rounded-lg w-72 focus:outline-none focus:ring-2 focus:ring-[color:var(--hl-orange)]/40 focus:bg-white transition"
                />
              </div>
              <button className="relative text-slate-500 hover:text-slate-800 p-1.5" data-testid="header-bell" aria-label="Notifications">
                <Bell className="w-5 h-5" />
                <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
              </button>
              <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
                <div
                  className="w-9 h-9 rounded-full text-white grid place-items-center text-[11px] font-bold shadow-md"
                  data-testid="header-avatar"
                  style={{ background: `linear-gradient(135deg, ${primary} 40%, ${accent} 140%)` }}
                >
                  {(user?.full_name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="hidden sm:block leading-tight max-w-[160px]">
                  <div className="text-sm font-semibold text-slate-800 truncate" data-testid="header-user-name">
                    {user?.full_name}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-[0.22em] truncate">
                    {ROLE_LABEL[user?.role] || user?.role}
                  </div>
                </div>
                <button className="ml-1 p-1.5 text-slate-500 hover:text-red-600 transition" data-testid="header-logout" onClick={handleLogout} aria-label="Sign out">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 edu360-main animate-fade-in-up relative" data-testid="portal-main">
          {/* Subtle backdrop gradient */}
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-60" aria-hidden
            style={{ background: `radial-gradient(circle at 20% 0%, ${accent}0d 0%, transparent 45%), radial-gradient(circle at 80% 20%, ${primary}0a 0%, transparent 55%)` }} />
          <Outlet />
        </main>

        <footer className="px-4 sm:px-6 py-4 border-t border-slate-200/60 text-[10px] uppercase tracking-[0.28em] text-slate-400 flex flex-wrap items-center justify-between gap-2" data-testid="edu360-footer">
          <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" style={{ color: accent }} />Executive Intelligence for International Education</span>
          <span className="normal-case tracking-normal text-slate-400">© {new Date().getFullYear()} {tenant?.name || "Education360"}</span>
        </footer>
      </div>
    </div>
  );
}
